import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Rocket, Megaphone, Pause, Play, BarChart3, Receipt } from "lucide-react";
import { toast } from "sonner";
import { api, formatPrice, formatDate, errText, imgUrl } from "../lib/api";
import { BoostModal, boostRemaining } from "../components/BoostModal";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";

const card = "bg-white border border-border rounded-2xl p-5 shadow-[0_1px_3px_rgba(0,0,0,0.04)]";
const BOOST_STATUS = { ACTIVE: "Aktiv", EXPIRED: "Abgelaufen", CANCELLED: "Erstattet" };
const CAMP_STATUS = { ACTIVE: "Aktiv", PAUSED: "Pausiert", COMPLETED: "Beendet", CANCELLED: "Erstattet" };

function StatGrid({ stats, cost }) {
  const rows = [
    ["Impressionen", stats.impressions], ["Aufrufe", stats.views], ["Favoriten", stats.favorites],
    ["Angebote", stats.offers], ["Nachrichten", stats.messages], ["Verkäufe", stats.sales],
    ["CTR", `${stats.ctr} %`], ...(cost != null ? [["Kosten", formatPrice(cost)]] : []),
  ];
  return (
    <div className="grid grid-cols-2 gap-2" data-testid="ad-stats-grid">
      {rows.map(([k, v]) => (
        <div key={k} className="bg-secondary/50 rounded-xl px-3 py-2.5">
          <p className="text-lg font-bold">{v}</p>
          <p className="text-xs text-muted-foreground">{k}</p>
        </div>
      ))}
    </div>
  );
}

function CampaignDialog({ items, config, onClose }) {
  const [selected, setSelected] = useState([]);
  const [budget, setBudget] = useState(config.campaign_budgets?.[1] || 20);
  const [custom, setCustom] = useState("");
  const [duration, setDuration] = useState(7);
  const [busy, setBusy] = useState(false);
  const activeItems = items.filter((i) => i.status === "ACTIVE");
  const finalBudget = custom ? parseFloat(custom.replace(",", ".")) : budget;

  const start = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/ads/campaign/checkout", {
        budget: finalBudget, duration_days: duration, item_ids: selected, origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(errText(e)); setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="campaign-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Megaphone className="w-5 h-5 text-accent" /> Shop bewerben</DialogTitle></DialogHeader>
        <p className="text-xs font-semibold">1. Artikel auswählen ({selected.length})</p>
        <div className="max-h-48 overflow-y-auto space-y-1.5 border border-border rounded-xl p-2">
          {activeItems.length === 0 && <p className="text-sm text-muted-foreground p-2">Keine aktiven Artikel vorhanden.</p>}
          {activeItems.map((i) => (
            <label key={i.id} data-testid={`campaign-item-${i.id}`} className="flex items-center gap-3 px-2 py-1.5 rounded-lg hover:bg-secondary cursor-pointer">
              <input type="checkbox" className="accent-[#FF6B35]" checked={selected.includes(i.id)}
                onChange={(e) => setSelected(e.target.checked ? [...selected, i.id] : selected.filter((x) => x !== i.id))} />
              {i.image_ids?.[0] && <img src={imgUrl(i.image_ids[0], "thumb")} alt="" className="w-9 h-9 rounded-lg object-cover" />}
              <span className="text-sm flex-1 truncate">{i.title}</span>
              <span className="text-sm font-semibold">{formatPrice(i.price)}</span>
            </label>
          ))}
        </div>
        <p className="text-xs font-semibold mt-2">2. Budget</p>
        <div className="flex flex-wrap gap-2">
          {(config.campaign_budgets || [10, 20, 50, 100]).map((b) => (
            <button key={b} data-testid={`campaign-budget-${b}`} onClick={() => { setBudget(b); setCustom(""); }}
              className={`px-4 py-2 rounded-full border text-sm font-semibold ${!custom && budget === b ? "border-accent text-accent bg-accent/5" : "border-border"}`}>
              {formatPrice(b)}
            </button>
          ))}
          <input data-testid="campaign-budget-custom" placeholder="Eigenes Budget €" value={custom} onChange={(e) => setCustom(e.target.value)}
            className="w-36 rounded-full border border-border px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-accent/50" />
        </div>
        <p className="text-xs font-semibold mt-2">3. Dauer</p>
        <div className="flex flex-wrap gap-2">
          {(config.campaign_durations || [3, 7, 14, 30]).map((d) => (
            <button key={d} data-testid={`campaign-duration-${d}`} onClick={() => setDuration(d)}
              className={`px-4 py-2 rounded-full border text-sm font-semibold ${duration === d ? "border-accent text-accent bg-accent/5" : "border-border"}`}>
              {d} Tage
            </button>
          ))}
        </div>
        <div className="bg-secondary/50 rounded-xl p-3 text-sm mt-2" data-testid="campaign-summary">
          <p><span className="font-bold">{selected.length}</span> Artikel · <span className="font-bold">{duration} Tage</span> · Budget <span className="font-bold">{finalBudget > 0 ? formatPrice(finalBudget) : "—"}</span></p>
          <p className="text-xs text-muted-foreground mt-1">Dein Budget wird nach Sichtbarkeit verbraucht und niemals überschritten. Du kannst jederzeit pausieren.</p>
        </div>
        <button data-testid="campaign-start-btn" onClick={start} disabled={busy || selected.length === 0 || !(finalBudget >= 5)}
          className="w-full bg-accent text-white font-bold py-3.5 rounded-full hover:opacity-90 disabled:opacity-50">
          {busy ? "Weiterleitung zur Zahlung…" : "Kampagne starten"}
        </button>
        {finalBudget > 0 && finalBudget < 5 && <p className="text-xs text-destructive text-center">Mindestbudget: 5,00 €</p>}
      </DialogContent>
    </Dialog>
  );
}

export default function Werbung() {
  const [data, setData] = useState(null);
  const [statsFor, setStatsFor] = useState(null);
  const [campaignOpen, setCampaignOpen] = useState(false);

  const load = useCallback(() => {
    api.get("/ads/my").then(({ data }) => setData(data)).catch(() => setData(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const pauseResume = async (c, action) => {
    try {
      await api.post(`/ads/campaign/${c.id}/${action}`);
      toast.success(action === "pause" ? "Kampagne pausiert" : "Kampagne fortgesetzt");
      load();
    } catch (e) { toast.error(errText(e)); }
  };

  if (data === null) return <div className="max-w-5xl mx-auto px-4 py-10"><div className="h-64 bg-muted rounded-2xl animate-pulse" /></div>;
  if (data === false) return <div className="max-w-5xl mx-auto px-4 py-20 text-center text-muted-foreground">Werbedaten konnten nicht geladen werden.</div>;

  return (
    <div className="bg-[#FAF9F5] min-h-screen">
      <div className="relative bg-[#111111] text-white overflow-hidden">
        <div aria-hidden="true" className="absolute -top-28 -right-28 w-[400px] h-[400px] rounded-full opacity-25" style={{ background: "radial-gradient(circle, #FF6B35 0%, transparent 65%)" }} />
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 py-10 md:py-14">
          <p className="fade-up inline-flex items-center gap-2 text-[11px] font-bold tracking-[0.25em] uppercase text-accent"><Rocket className="w-4 h-4" /> Werbung & Boosts</p>
          <h1 className="fade-up-1 font-display text-3xl md:text-5xl font-bold tracking-tight mt-3 leading-[1.08]" data-testid="ads-heading">Mehr Sichtbarkeit für deine Artikel.</h1>
          <p className="fade-up-2 text-sm md:text-lg text-white/70 mt-3 max-w-xl">Booste einzelne Artikel oder bewirb deinen Shop mit einer Kampagne — Preise siehst du immer vor der Buchung.</p>
        </div>
      </div>
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8 pb-24">
        <Tabs defaultValue="boosts" className="mt-6">
          <TabsList className="rounded-full">
            <TabsTrigger data-testid="ads-tab-boosts" value="boosts" className="rounded-full">Boosts</TabsTrigger>
            <TabsTrigger data-testid="ads-tab-campaigns" value="campaigns" className="rounded-full">Kampagnen</TabsTrigger>
            <TabsTrigger data-testid="ads-tab-payments" value="payments" className="rounded-full">Zahlungen</TabsTrigger>
          </TabsList>

          <TabsContent value="boosts" className="mt-5 space-y-3">
            {data.items.length === 0 && (
              <div className={card}><p className="text-sm text-muted-foreground">Du hast keine aktiven Artikel. <Link to="/verkaufen" className="text-accent font-semibold hover:underline">Jetzt Artikel einstellen</Link></p></div>
            )}
            {data.items.map((it) => (
              <div key={it.id} className={`${card} flex items-center gap-4 flex-wrap`} data-testid={`ads-item-${it.id}`}>
                {it.image_ids?.[0] && <img src={imgUrl(it.image_ids[0], "thumb")} alt="" className="w-14 h-14 rounded-xl object-cover border border-border" />}
                <div className="flex-1 min-w-0">
                  <Link to={`/artikel/${it.id}`} className="text-sm font-semibold line-clamp-1 hover:text-accent">{it.title}</Link>
                  <p className="text-sm font-bold">{formatPrice(it.price)}</p>
                </div>
                {it.active_boost ? (
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs font-bold text-accent bg-accent/10 px-3 py-1.5 rounded-full flex items-center gap-1.5" data-testid={`boost-active-${it.id}`}>
                      <Rocket className="w-3.5 h-3.5" /> Boost aktiv · noch {boostRemaining(it.active_boost.end_at)}
                    </span>
                    <BoostModal itemId={it.id} extend
                      trigger={<button data-testid={`boost-extend-${it.id}`} className="text-xs font-bold border border-border rounded-full px-3.5 py-1.5 hover:bg-secondary">Verlängern</button>} />
                  </div>
                ) : (
                  <BoostModal itemId={it.id}
                    trigger={<button data-testid={`boost-btn-${it.id}`} className="bg-foreground text-white text-xs font-bold px-4 py-2 rounded-full hover:opacity-90 flex items-center gap-1.5"><Rocket className="w-3.5 h-3.5" /> Artikel boosten</button>} />
                )}
              </div>
            ))}
            {data.boosts.length > 0 && (
              <div className={card}>
                <p className="font-bold text-sm mb-3 flex items-center gap-2"><BarChart3 className="w-4 h-4 text-accent" /> Deine Boosts</p>
                <div className="space-y-2">
                  {data.boosts.map((b) => (
                    <div key={b.id} className="flex items-center justify-between gap-3 border border-border/70 rounded-xl px-3.5 py-2.5 flex-wrap" data-testid={`boost-row-${b.id}`}>
                      <div className="min-w-0">
                        <p className="text-sm font-medium line-clamp-1">{b.item_title}</p>
                        <p className="text-xs text-muted-foreground">{b.days} Tage · {formatPrice(b.price)} · {BOOST_STATUS[b.status] || b.status}{b.status === "ACTIVE" && ` · noch ${boostRemaining(b.end_at)}`}</p>
                      </div>
                      <button data-testid={`boost-stats-${b.id}`} onClick={() => setStatsFor({ title: b.item_title, stats: b.stats, cost: b.price })}
                        className="text-xs font-bold border border-border rounded-full px-3.5 py-1.5 hover:bg-secondary">Statistiken</button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </TabsContent>

          <TabsContent value="campaigns" className="mt-5 space-y-3">
            <button data-testid="new-campaign-btn" onClick={() => setCampaignOpen(true)}
              className="bg-accent text-white text-sm font-bold px-5 py-2.5 rounded-full hover:opacity-90 flex items-center gap-2">
              <Megaphone className="w-4 h-4" /> Shop bewerben
            </button>
            {data.campaigns.length === 0 && <div className={card}><p className="text-sm text-muted-foreground">Noch keine Kampagnen. Bewirb mehrere Artikel gleichzeitig mit einem festen Budget.</p></div>}
            {data.campaigns.map((c) => (
              <div key={c.id} className={card} data-testid={`campaign-row-${c.id}`}>
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div>
                    <p className="text-sm font-bold">Kampagne · {c.item_ids.length} Artikel · {c.duration_days} Tage</p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Status: <span className="font-semibold text-foreground">{CAMP_STATUS[c.status] || c.status}</span> · Budget {formatPrice(c.budget)} · Ausgegeben {formatPrice(Math.min(c.spent, c.budget))} · Rest {formatPrice(c.remaining_budget)}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    {c.status === "ACTIVE" && <button data-testid={`campaign-pause-${c.id}`} onClick={() => pauseResume(c, "pause")} className="text-xs font-bold border border-border rounded-full px-3.5 py-1.5 hover:bg-secondary flex items-center gap-1"><Pause className="w-3 h-3" /> Pausieren</button>}
                    {c.status === "PAUSED" && <button data-testid={`campaign-resume-${c.id}`} onClick={() => pauseResume(c, "resume")} className="text-xs font-bold border border-accent text-accent rounded-full px-3.5 py-1.5 hover:bg-accent/5 flex items-center gap-1"><Play className="w-3 h-3" /> Fortsetzen</button>}
                    <button data-testid={`campaign-stats-${c.id}`} onClick={() => setStatsFor({ title: `Kampagne (${c.item_ids.length} Artikel)`, stats: c.stats, cost: c.spent })}
                      className="text-xs font-bold border border-border rounded-full px-3.5 py-1.5 hover:bg-secondary">Statistiken</button>
                  </div>
                </div>
                <div className="h-2 bg-secondary rounded-full mt-3 overflow-hidden">
                  <div className="h-full bg-accent rounded-full" style={{ width: `${Math.min(100, (c.spent / c.budget) * 100)}%` }} />
                </div>
              </div>
            ))}
          </TabsContent>

          <TabsContent value="payments" className="mt-5">
            <div className={card}>
              <p className="font-bold text-sm mb-3 flex items-center gap-2"><Receipt className="w-4 h-4 text-accent" /> Werbekosten</p>
              {data.payments.length === 0 && <p className="text-sm text-muted-foreground">Noch keine Werbezahlungen.</p>}
              <div className="space-y-2" data-testid="ad-payments-list">
                {data.payments.map((p) => (
                  <div key={p.id} className="flex items-center justify-between text-sm border-b border-border/60 last:border-0 py-2.5 gap-3">
                    <div className="min-w-0">
                      <p className="font-medium line-clamp-1">{p.label}</p>
                      <p className="text-xs text-muted-foreground">{formatDate(p.created_at)}</p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="font-bold">{formatPrice(p.amount)}</p>
                      <p className={`text-xs font-semibold ${p.payment_status === "paid" ? "text-green-600" : p.payment_status === "refunded" ? "text-amber-600" : "text-muted-foreground"}`}>
                        {p.payment_status === "paid" ? "Bezahlt" : p.payment_status === "refunded" ? "Erstattet" : "Offen"}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {statsFor && (
        <Dialog open onOpenChange={(v) => !v && setStatsFor(null)}>
          <DialogContent className="max-w-md">
            <DialogHeader><DialogTitle className="text-base">{statsFor.title}</DialogTitle></DialogHeader>
            <StatGrid stats={statsFor.stats} cost={statsFor.cost} />
          </DialogContent>
        </Dialog>
      )}
      {campaignOpen && <CampaignDialog items={data.items} config={data.config} onClose={() => { setCampaignOpen(false); load(); }} />}
    </div>
  );
}

export function AdsSuccess() {
  const nav = useNavigate();
  const [status, setStatus] = useState("checking");
  const [info, setInfo] = useState(null);

  useEffect(() => {
    const sessionId = new URLSearchParams(window.location.search).get("session_id");
    if (!sessionId) return setStatus("error");
    let attempts = 0;
    const poll = async () => {
      if (attempts++ > 10) return setStatus("error");
      try {
        const { data } = await api.get(`/ads/payments/status/${sessionId}`);
        if (data.payment_status === "paid") { setInfo(data); return setStatus("paid"); }
      } catch {}
      setTimeout(poll, 2000);
    };
    poll();
  }, []);

  return (
    <div className="max-w-md mx-auto px-4 py-20 text-center">
      {status === "checking" && <><div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto" /><p className="mt-6 font-semibold">Zahlung wird bestätigt…</p></>}
      {status === "paid" && (
        <div data-testid="ads-payment-success">
          <div className="w-16 h-16 bg-accent text-white rounded-full flex items-center justify-center mx-auto"><Rocket className="w-7 h-7" /></div>
          <h1 className="text-3xl font-bold mt-6">{info?.type === "CAMPAIGN" ? "Kampagne aktiv!" : "Boost aktiv!"}</h1>
          <p className="text-muted-foreground text-sm mt-3">{info?.label} · {formatPrice(info?.amount || 0)} — deine Artikel erhalten jetzt zusätzliche Sichtbarkeit.</p>
          <button data-testid="goto-ads-btn" onClick={() => nav("/werbung")} className="mt-8 bg-primary text-primary-foreground font-bold px-8 py-3.5 rounded-full hover:opacity-90">Zur Werbeübersicht</button>
        </div>
      )}
      {status === "error" && (
        <div data-testid="ads-payment-error">
          <h1 className="text-3xl font-bold">Hmm…</h1>
          <p className="text-muted-foreground text-sm mt-3">Die Zahlung konnte nicht bestätigt werden. Prüfe die Werbeübersicht in ein paar Minuten.</p>
          <button onClick={() => nav("/werbung")} className="mt-8 border border-border font-bold px-8 py-3.5 rounded-full hover:bg-secondary">Zur Werbeübersicht</button>
        </div>
      )}
    </div>
  );
}
